import React from 'react';
import { Helmet } from 'react-helmet-async';
import { DOMAIN, BRAND, DEFAULT_OG_IMAGE } from '../../seo/siteConfig';

/**
 * Seo renders all per-page metadata: title, description, canonical, Open Graph,
 * Twitter Card, and JSON-LD (Organization always, plus optional BreadcrumbList,
 * FAQPage, and Service / LocalBusiness). Driven entirely by centralized page data
 * so a future SSR/prerender layer can reuse the same objects.
 */
const Seo = ({
  title,
  description,
  canonicalPath,
  image = DEFAULT_OG_IMAGE,
  breadcrumbs = [],
  faqs = [],
  schema = null, // { type: 'Service'|'LocalBusiness'|'Article', data:{...} }
}) => {
  const url = DOMAIN + (canonicalPath || '/');

  const organization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: BRAND.name,
    url: DOMAIN,
    email: BRAND.email,
    telephone: BRAND.generalPhone,
    description: BRAND.descriptor,
    areaServed: BRAND.region,
    logo: DEFAULT_OG_IMAGE,
  };

  const graphs = [organization];

  if (breadcrumbs && breadcrumbs.length) {
    graphs.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((b, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: b.label,
        item: DOMAIN + b.path,
      })),
    });
  }

  if (faqs && faqs.length) {
    graphs.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  if (schema && schema.type === 'Service') {
    graphs.push({
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: schema.data.serviceType || 'Recovery Housing',
      name: schema.data.name || title,
      areaServed: schema.data.areaServed || BRAND.region,
      provider: { '@type': 'Organization', name: BRAND.name, url: DOMAIN },
      description: description,
    });
  }

  if (schema && schema.type === 'LocalBusiness') {
    const d = schema.data || {};
    const addr = { '@type': 'PostalAddress', addressLocality: d.city, addressRegion: d.state, postalCode: d.zip, addressCountry: 'US' };
    if (d.street) addr.streetAddress = d.street;
    graphs.push({
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: d.name || BRAND.name,
      url: url,
      telephone: d.phone || BRAND.generalPhone,
      email: BRAND.email,
      address: addr,
      areaServed: BRAND.region,
      description: description,
    });
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:site_name" content={BRAND.name} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {graphs.map((g, i) => (
        <script type="application/ld+json" key={i}>{JSON.stringify(g)}</script>
      ))}
    </Helmet>
  );
};

export default Seo;
